import React, { useState, useEffect } from 'react';
import { FaFileInvoiceDollar, FaDownload, FaExternalLinkAlt, FaCheckCircle, FaTimesCircle, FaClock } from 'react-icons/fa';
import apiClient from '../../services/apiClient';

const statusMap = {
  paid: { label: 'Platit', icon: FaCheckCircle, color: 'text-green-600 bg-green-50' },
  succeeded: { label: 'Reusit', icon: FaCheckCircle, color: 'text-green-600 bg-green-50' },
  open: { label: 'In asteptare', icon: FaClock, color: 'text-amber-600 bg-amber-50' },
  draft: { label: 'Draft', icon: FaClock, color: 'text-gray-600 bg-gray-50' },
  void: { label: 'Anulat', icon: FaTimesCircle, color: 'text-red-600 bg-red-50' },
  uncollectible: { label: 'Neincasabil', icon: FaTimesCircle, color: 'text-red-600 bg-red-50' },
  failed: { label: 'Esuat', icon: FaTimesCircle, color: 'text-red-600 bg-red-50' },
};

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const response = await apiClient.get('/api/subscription/PaymentHistory');
        setPayments(response.data.payments || []);
      } catch (err) {
        console.error('Error fetching payments:', err);
        setError('Nu s-au putut incarca platile.');
      } finally {
        setLoading(false);
      }
    };
    fetchPayments();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
          <FaFileInvoiceDollar className="text-indigo-500" /> Istoric Plati
        </h2>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
        <FaFileInvoiceDollar className="text-indigo-500" /> Istoric Plati
      </h2>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
      )}

      {payments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-2xl">
          <FaFileInvoiceDollar className="text-4xl text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Nu exista plati inregistrate.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <th className="text-left p-3 font-semibold text-gray-700">Data</th>
                <th className="text-left p-3 font-semibold text-gray-700">Descriere</th>
                <th className="text-left p-3 font-semibold text-gray-700">Tip</th>
                <th className="text-right p-3 font-semibold text-gray-700">Suma</th>
                <th className="text-center p-3 font-semibold text-gray-700">Status</th>
                <th className="text-center p-3 font-semibold text-gray-700">Actiuni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {payments.map((payment) => {
                const statusInfo = statusMap[payment.status] || statusMap.draft;
                const StatusIcon = statusInfo.icon;
                const date = new Date(payment.date);

                return (
                  <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-3 whitespace-nowrap">
                      <div className="font-medium text-gray-800">{date.toLocaleDateString('ro-RO')}</div>
                      <div className="text-xs text-gray-500">{date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-gray-800 max-w-xs truncate" title={payment.description}>
                        {payment.description}
                      </div>
                      {payment.subscriptionId && (
                        <div className="text-xs text-indigo-500 mt-0.5">Abonament</div>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        payment.type === 'invoice' ? 'bg-indigo-100 text-indigo-700' :
                        payment.type === 'credit_purchase' ? 'bg-purple-100 text-purple-700' :
                        payment.type === 'subscription' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {payment.type === 'invoice' ? 'Factura abonament' :
                         payment.type === 'credit_purchase' ? 'Cumparare credite' :
                         payment.type === 'subscription' ? 'Abonament' : 'Plata'}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="font-bold text-gray-800">{payment.amount?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</span>
                      <span className="text-xs text-gray-500 ml-1">{payment.currency}</span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${statusInfo.color}`}>
                        <StatusIcon size={12} /> {statusInfo.label}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {payment.invoiceUrl && (
                          <a href={payment.invoiceUrl} target="_blank" rel="noopener noreferrer"
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition" title="Vezi factura">
                            <FaExternalLinkAlt size={14} />
                          </a>
                        )}
                        {payment.invoicePdf && (
                          <a href={payment.invoicePdf} target="_blank" rel="noopener noreferrer"
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition" title="Descarca PDF">
                            <FaDownload size={14} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 text-center">
        Platile sunt sincronizate din Stripe. Facturile pot fi descarcate in format PDF.
      </p>
    </div>
  );
};

export default PaymentHistory;
